import { useCallback, type FormHTMLAttributes, type ReactNode, type SyntheticEvent } from 'react';
import { FormContext } from './FormContext.js';
import { useFormState } from './useFormState.js';
import type { FormErrors, FormValues, ValidateOn } from './types.js';

export interface FormProps extends Omit<
  FormHTMLAttributes<HTMLFormElement>,
  'onSubmit' | 'action'
> {
  /** Uncontrolled initial values. */
  defaultValues?: FormValues;
  /** Controlled values — parent owns the values and must update them through
   *  `onValuesChange`. Mutually exclusive with `defaultValues`. */
  values?: FormValues;
  onValuesChange?: (values: FormValues) => void;
  /** Called after field validation passes on submit. */
  onSubmit?: (values: FormValues) => void | Promise<void>;
  /** React 19 Server / client Action. If provided, the form delegates to
   *  `<form action>`; `onSubmit` is still invoked after validation passes. */
  action?: (formData: FormData) => void | Promise<void>;
  /** When fields are revalidated. Default `'blur'`. */
  validateOn?: ValidateOn;
  /** Form-level validator — runs alongside per-field validators. */
  validate?: (values: FormValues) => FormErrors | Promise<FormErrors>;
  children: ReactNode;
}

function nativeValuesFromForm(form: HTMLFormElement): FormValues {
  const values: FormValues = {};
  for (const element of Array.from(form.elements)) {
    if (
      !(
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement
      )
    ) {
      continue;
    }
    if (element.name.length === 0 || element.disabled) continue;

    if (element instanceof HTMLInputElement) {
      if (element.type === 'file') continue;
      if (element.type === 'checkbox') {
        values[element.name] = element.checked;
        continue;
      }
      if (element.type === 'radio') {
        if (element.checked) values[element.name] = element.value;
        continue;
      }
    }

    if (element instanceof HTMLSelectElement && element.multiple) {
      values[element.name] = Array.from(element.selectedOptions, (option) => option.value);
      continue;
    }

    values[element.name] = element.value;
  }
  return values;
}

/** The top-level form component. Owns field registration, validation
 *  scheduling, submit flow, and provides the `FormApi` context. Spec 12. */
export function Form(props: FormProps): ReactNode {
  const {
    defaultValues,
    values,
    onValuesChange,
    onSubmit,
    action,
    validateOn,
    validate,
    children,
    ...rest
  } = props;

  const api = useFormState({
    ...(defaultValues !== undefined ? { defaultValues } : {}),
    ...(values !== undefined ? { controlledValues: values } : {}),
    ...(onValuesChange !== undefined ? { onValuesChange } : {}),
    ...(validateOn !== undefined ? { validateOn } : {}),
    ...(onSubmit !== undefined ? { onSubmit } : {}),
    ...(validate !== undefined ? { formValidate: validate } : {}),
  });

  const handleSubmit = useCallback(
    async (e: SyntheticEvent<HTMLFormElement>): Promise<void> => {
      if (action === undefined) e.preventDefault();
      // Delegate to the form API — it handles validation + touched +
      // onSubmit in the right order with submit-state bookkeeping.
      await api.submit(nativeValuesFromForm(e.currentTarget));
      if (Object.values(api.errors).some((err) => err !== undefined)) {
        e.preventDefault();
      }
    },
    [action, api],
  );

  return (
    <FormContext.Provider value={api}>
      <form
        {...rest}
        data-component="Form"
        action={action as unknown as string | undefined}
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        noValidate
      >
        {children}
      </form>
    </FormContext.Provider>
  );
}
